import { ChevronDown, ChevronRight } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router';
import { useCrumbs } from '../lib/crumbs';
import { DOC_GROUPS, DOC_PAGES, OLD_ANCHORS } from './pages';

interface Heading {
  id: string;
  text: string;
}

/** The headings of the article on screen, and the one being read. */
function useHeadings(article: HTMLElement | null, slug: string) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [current, setCurrent] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: read again for each page
  useLayoutEffect(() => {
    if (!article) return;
    const found = [...article.querySelectorAll<HTMLHeadingElement>('h2[id]')].map((h) => ({
      id: h.id,
      text: h.textContent ?? '',
    }));
    setHeadings(found);
    setCurrent(found[0]?.id ?? null);
  }, [article, slug]);

  useEffect(() => {
    if (!article || !headings.length || typeof IntersectionObserver === 'undefined') return;
    // The current heading is the last one above the top third of the screen.
    const observer = new IntersectionObserver(
      () => {
        let id = headings[0]?.id ?? null;
        for (const heading of headings) {
          const element = document.getElementById(heading.id);
          if (element && element.getBoundingClientRect().top < window.innerHeight / 3) {
            id = heading.id;
          }
        }
        setCurrent(id);
      },
      { rootMargin: '0px 0px -66% 0px', threshold: [0, 1] },
    );
    for (const heading of headings) {
      const element = document.getElementById(heading.id);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [article, headings]);

  return { headings, current };
}

const FOLDED_KEY = 'mogzo:docs-folded';

function readFolded(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(FOLDED_KEY) ?? '[]');
    return Array.isArray(saved) ? saved.filter((g) => typeof g === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * The groups of the pages list this reader folded, kept in their browser. Every group starts
 * open, and the one holding the page on screen opens again whenever a page is opened.
 */
function useFoldedGroups(currentGroup: string | undefined) {
  const [folded, setFolded] = useState(readFolded);

  useEffect(() => {
    if (currentGroup) setFolded((all) => all.filter((g) => g !== currentGroup));
  }, [currentGroup]);

  useEffect(() => {
    try {
      localStorage.setItem(FOLDED_KEY, JSON.stringify(folded));
    } catch {
      // Private windows can refuse; the folds then last until the page closes.
    }
  }, [folded]);

  function setOpen(group: string, open: boolean) {
    setFolded((all) =>
      open ? all.filter((g) => g !== group) : all.includes(group) ? all : [...all, group],
    );
  }
  return { folded, setOpen };
}

function PagesList({
  slug,
  folded,
  setOpen,
}: {
  slug: string;
  folded: string[];
  setOpen: (group: string, open: boolean) => void;
}) {
  return (
    <nav aria-label="Docs pages" className="mz-doc-nav">
      {DOC_GROUPS.map((group) => {
        const pages = DOC_PAGES.filter((page) => page.group === group);
        return (
          <details
            key={group}
            className="mz-doc-nav-group"
            open={!folded.includes(group)}
            onToggle={(event) => setOpen(group, event.currentTarget.open)}
          >
            <summary>
              <ChevronRight size={16} strokeWidth={1.75} aria-hidden="true" />
              <span>{group}</span>
              <span className="mz-doc-nav-count" aria-hidden="true">
                {pages.length}
              </span>
            </summary>
            <div className="mz-doc-nav-pages">
              {pages.map((page) => (
                <Link
                  key={page.slug}
                  to={page.slug === 'introduction' ? '/docs' : `/docs/${page.slug}`}
                  aria-current={page.slug === slug ? 'page' : undefined}
                >
                  {page.title}
                </Link>
              ))}
            </div>
          </details>
        );
      })}
    </nav>
  );
}

function OnThisPage({ headings, current }: { headings: Heading[]; current: string | null }) {
  if (!headings.length) return null;
  return (
    <nav aria-label="On this page" className="mz-toc">
      {headings.map((heading) => (
        <a
          key={heading.id}
          href={`#${heading.id}`}
          aria-current={heading.id === current ? 'true' : undefined}
        >
          {heading.text}
        </a>
      ))}
    </nav>
  );
}

/** dev.mogzo.com/docs and /docs/<page> (M26): a page per topic. */
export function DocsPage() {
  const { slug = 'introduction' } = useParams();
  const location = useLocation();
  const [article, setArticle] = useState<HTMLElement | null>(null);
  const navFold = useRef<HTMLDetailsElement>(null);
  const tocFold = useRef<HTMLDetailsElement>(null);
  const index = DOC_PAGES.findIndex((page) => page.slug === slug);
  const page = DOC_PAGES[index];
  const { headings, current } = useHeadings(article, slug);
  const { folded, setOpen } = useFoldedGroups(page?.group);

  useCrumbs(
    ...(page && page.slug !== 'introduction'
      ? [{ label: 'Docs', to: '/docs' }, { label: page.group }, { label: page.title }]
      : [{ label: 'Docs' }]),
  );

  // A new page starts at its top, or at the heading its link names; the folds close.
  // biome-ignore lint/correctness/useExhaustiveDependencies: on each page or heading
  useEffect(() => {
    navFold.current?.removeAttribute('open');
    tocFold.current?.removeAttribute('open');
    const target = location.hash ? document.getElementById(location.hash.slice(1)) : null;
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [slug, location.hash]);

  // The old one-page docs' links (/docs#saves) go to their page.
  const old = slug === 'introduction' && OLD_ANCHORS[location.hash.slice(1)];
  if (old) return <Navigate replace to={`/docs/${old}`} />;

  if (!page) {
    return (
      <main className="mz-shell-content">
        <h1 className="mz-pagehead-title">No docs page here</h1>
        <p>
          <Link to="/docs">See all the docs</Link>
        </p>
      </main>
    );
  }

  const previous = DOC_PAGES[index - 1];
  const next = DOC_PAGES[index + 1];
  const { Content } = page;
  const pageLink = (p: { slug: string }) =>
    p.slug === 'introduction' ? '/docs' : `/docs/${p.slug}`;

  return (
    <div className="mz-docs">
      <div className="mz-docs-nav">
        <PagesList slug={page.slug} folded={folded} setOpen={setOpen} />
      </div>

      <main className="mz-docs-main">
        <div className="mz-docs-folds">
          <details ref={navFold} className="mz-docs-fold mz-docs-fold-nav">
            <summary>
              <span>
                <span className="block text-[13px] font-normal text-muted">
                  Docs · {page.group}
                </span>
                {page.title}
              </span>
              <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" />
            </summary>
            <PagesList slug={page.slug} folded={folded} setOpen={setOpen} />
          </details>
          {headings.length > 0 && (
            <details ref={tocFold} className="mz-docs-fold mz-docs-fold-toc">
              <summary>
                <span className="text-[13px] text-muted">On this page</span>
                <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" />
              </summary>
              <div>
                <OnThisPage headings={headings} current={current} />
              </div>
            </details>
          )}
        </div>

        <article ref={setArticle} className="mz-doc">
          {page.slug !== 'introduction' && (
            <div className="mb-2 text-[13px] text-muted">{page.group}</div>
          )}
          <Content />
          <nav aria-label="More docs" className="mz-doc-pager">
            {previous && (
              <Link to={pageLink(previous)}>
                <span className="text-[13px] text-muted">Previous</span>
                <span className="font-medium">{previous.title}</span>
              </Link>
            )}
            {next && (
              <Link to={pageLink(next)} className="mz-doc-pager-next">
                <span className="text-[13px] text-muted">Next</span>
                <span className="font-medium">{next.title}</span>
              </Link>
            )}
          </nav>
        </article>
      </main>

      <aside className="mz-docs-toc">
        {headings.length > 0 && (
          <div className="mz-nav-label" aria-hidden="true">
            ON THIS PAGE
          </div>
        )}
        <OnThisPage headings={headings} current={current} />
      </aside>
    </div>
  );
}
